import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from './api';
import { toast } from '../utils/toast';
import { extractApiError } from '../utils/apiError';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MediaItem {
  id: number;
  user: number;
  user_id?: number; // Backend sometimes returns user_id
  full_name?: string; // Flattened owner name
  username?: string; // Flattened owner username
  title?: string;
  caption?: string; // Backend returns caption for images
  description?: string;
  media_type: 'image' | 'video' | 'audio';
  file_url?: string;
  media_urls?: string[]; // Backend returns an array of URLs
  /** Poster/thumbnail URLs for video items (parallel to media_urls) */
  video_poster_urls?: string[] | null;
  is_verified?: boolean;
  order?: number;
  collaborators?: UserSearchResult[]; // Nested collaborators
  /** Creator profile (newer API format) */
  creator?: {
    id: number;
    username?: string;
    full_name?: string;
    profile_image?: string;
  };
  service?: {
    // Nested service (singular, as returned by backend)
    id: number;
    name: string;
    price?: string;
    description?: string;
  };
  event?: {
    // Nested event — shape differs by endpoint
    id?: number;
    event_uuid?: string;
    event_title?: string;
    name?: string;
    description?: string;
    slug?: string;
    featured_image?: {
      id?: number | string;
      media_type?: string;
      file_url?: string;
    } | null;
  };
  /** Whether the current user follows the media creator */
  is_following_creator?: boolean;
  created_at: string;
  // ── Platform links ──
  website?: string;
  spotify?: string;
  soundcloud?: string;
  apple_music?: string;
  amazon_music?: string;
  youtube_music?: string;
}

/** Payload for updating a media item via useUpdateMedia */
export interface UpdateMediaPayload {
  mediaId: number | string;
  fields: Partial<MediaItem> & {
    event_id?: number | null;
    collaborator_ids?: number[];
    service_ids?: number[];
  };
}

/** Standard JSON payload for creating media (used after Cloudinary upload) */
export interface CreateMediaPayload {
  title?: string;
  caption?: string;
  description?: string;
  media_type: 'image' | 'video' | 'audio';
  media_urls: string[];
  event_id?: number;
  service_id?: number;
  collaborator_ids?: number[];
  // ── Platform links ──
  website?: string;
  spotify?: string;
  soundcloud?: string;
  apple_music?: string;
  amazon_music?: string;
  youtube_music?: string;
}

/** Alias for structural compatibility with older parts of the app */
export type MediaDto = MediaItem;

export interface UserSearchResult {
  id: number;
  full_name?: string;
  username?: string;
  prof_pics_url?: string | null;
  prof_pics?: string | null;
  user_types?: ({ id?: number; name: string } | string)[];
  city?: string | null;
}

export interface UserServiceItem {
  id: number;
  title: string;
  description?: string;
  price?: string;
}

export interface UserEventItem {
  id: number;
  event_title?: string;
  location?: string;
  start?: string;
  medias?: Array<{ file_url?: string; image?: string }>;
}

export interface MediaListResponse {
  results: MediaItem[];
  count: number;
  next: string | null;
  previous: string | null;
}

export interface UserEventsResponse {
  results: UserEventItem[];
  count: number;
  next: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Helper to guess if a URL is a video. */
export const isVideoUrl = (url?: string | string[]): boolean => {
  if (!url) return false;
  const target = (Array.isArray(url) ? url[0] : url)?.trim();
  if (!target) return false;
  const normalized = target.toLowerCase();
  const videoExtRegex = /\.(mp4|mov|avi|webm|m4v|mkv)(\?.*)?$/i;
  return (
    videoExtRegex.test(normalized) ||
    normalized.includes('/video/upload/') ||
    normalized.includes('resource_type=video') ||
    normalized.includes('content_type=video')
  );
};

// ─── Media CRUD ───────────────────────────────────────────────────────────────

/** Create new media (JSON for URLs, or multipart for files). */
export const useCreateMedia = () => {
  const queryClient = useQueryClient();
  return useMutation<MediaItem, Error, CreateMediaPayload | FormData>({
    mutationFn: async (payload) => {
      const isFormData = payload instanceof FormData;
      console.log(
        `[API] Creating media via /profiles/media/ (${isFormData ? 'Multipart' : 'JSON'})`,
      );
      const { data } = await apiClient.post('/profiles/media/', payload, {
        headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
      });
      console.log('[API] Create media response:', data);
      return data;
    },
    onSuccess: () => {
      toast.success('Media published successfully');
      queryClient.invalidateQueries({ queryKey: ['media'] });
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Upload failed'));
    },
  });
};

/** Create media from an existing Cloudinary URL or other. */
export const useCreateMediaJson = () => {
  const queryClient = useQueryClient();
  return useMutation<
    MediaItem,
    Error,
    {
      title: string;
      description?: string;
      media_type: 'image' | 'video';
      file_url: string;
    }
  >({
    mutationFn: async (payload) => {
      console.log('[API] Creating media via /profiles/media/ (JSON):', payload);
      const { data } = await apiClient.post('/profiles/media/', payload);
      console.log('[API] Create media response:', data);
      return data;
    },
    onSuccess: () => {
      toast.success('Media created successfully');
      queryClient.invalidateQueries({ queryKey: ['media'] });
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Creation failed'));
    },
  });
};

/** Get authenticated user's media. */
export const useGetMyMedia = (page = 1, options?: { enabled?: boolean }) => {
  return useQuery<MediaListResponse>({
    queryKey: ['media', 'my', page],
    enabled: options?.enabled !== false,
    queryFn: async () => {
      console.log('[API] Fetching logged-in user media via /profiles/media/');
      const { data } = await apiClient.get(`/profiles/media/?page=${page}&page_size=20`);
      console.log('[API] Logged-in user media response:', data);
      return data;
    },
  });
};

/** Get a specific user's media. */
export const useGetUserMedia = (userId: string | number, page = 1) => {
  return useQuery<MediaListResponse>({
    queryKey: ['media', 'user', userId, page],
    queryFn: async () => {
      if (!userId) {
        console.warn('[API] useGetUserMedia: No userId provided');
        return { results: [], count: 0, next: null, previous: null };
      }
      console.log(`[API] Fetching media for userId ${userId} via /profiles/media/user/${userId}/`);
      const { data } = await apiClient.get(
        `/profiles/media/user/${userId}/?page=${page}&page_size=20`,
      );
      console.log(`[API] Media response for ${userId}:`, data);
      return data;
    },
    enabled: !!userId,
  });
};

/** Get a single media item. */
export const useGetMediaDetail = (mediaId: number | string) => {
  return useQuery<MediaItem>({
    queryKey: ['media', String(mediaId)],
    queryFn: async () => {
      console.log(`[API] Fetching media detail for ${mediaId} via /profiles/media/${mediaId}/`);
      const { data } = await apiClient.get(`/profiles/media/${mediaId}/`);
      console.log(`[API] Media detail response for ${mediaId}:`, data);
      return data;
    },
    enabled: !!mediaId,
  });
};

/** Get a single public media item — no auth required. */
export const useGetPublicMediaDetail = (mediaId: number | string) => {
  return useQuery<MediaItem>({
    queryKey: ['media', 'public', String(mediaId)],
    queryFn: async () => {
      console.log(
        `[API] Fetching public media detail for ${mediaId} via /public/media/${mediaId}/`,
      );
      const { data } = await apiClient.get(`/public/media/${mediaId}/`);
      console.log(`[API] Public media detail response for ${mediaId}:`, data);
      // Response: { success: true, media: { ... } }
      return (data?.media ?? data?.data ?? data?.results ?? data) as MediaItem;
    },
    enabled: !!mediaId,
  });
};

/** Get all public media — no auth required. */
export const useGetPublicMedia = (page = 1, options?: { enabled?: boolean; pageSize?: number }) => {
  const pageSize = options?.pageSize ?? 20;
  return useQuery<MediaListResponse>({
    queryKey: ['media', 'public-list', page, pageSize],
    enabled: options?.enabled !== false,
    queryFn: async () => {
      console.log(
        `[API] Fetching public media via /public/media/?page=${page}&page_size=${pageSize}`,
      );
      const { data } = await apiClient.get(`/public/media/?page=${page}&page_size=${pageSize}`);
      console.log('[API] Public media response:', data);
      // API may return double-nested: { count, next, results: { success, results: [...] } }
      const inner = data?.results;
      const results: MediaItem[] = Array.isArray(data)
        ? data
        : Array.isArray(inner)
          ? inner
          : Array.isArray((inner as any)?.results)
            ? (inner as any).results
            : Array.isArray(data?.data)
              ? data.data
              : [];
      return {
        results,
        count: data?.count ?? results.length,
        next: data?.next ?? null,
        previous: data?.previous ?? null,
      };
    },
  });
};

/** Update a media item. */
export const useUpdateMedia = () => {
  const queryClient = useQueryClient();
  return useMutation<MediaItem, Error, UpdateMediaPayload>({
    mutationFn: async ({ mediaId, fields }) => {
      console.log(`[API] Updating media ${mediaId} via /profiles/media/${mediaId}/:`, fields);
      const { data } = await apiClient.patch(`/profiles/media/${mediaId}/`, fields);
      console.log(`[API] Update media response for ${mediaId}:`, data);
      return data;
    },
    onSuccess: (_data, vars) => {
      toast.success('Media updated');
      queryClient.invalidateQueries({ queryKey: ['media'] });
      queryClient.invalidateQueries({ queryKey: ['media', String(vars.mediaId)] });
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Update failed'));
    },
  });
};

/** Delete a media item. */
export const useDeleteMedia = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number | string>({
    mutationFn: async (mediaId) => {
      console.log(`[API] Deleting media ${mediaId} via /profiles/media/${mediaId}/`);
      await apiClient.delete(`/profiles/media/${mediaId}/`);
      console.log(`[API] Delete media ${mediaId} successful`);
    },
    onSuccess: () => {
      toast.success('Media deleted');
      queryClient.invalidateQueries({ queryKey: ['media'] });
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Deletion failed'));
    },
  });
};

// ─── Auxiliary Selectors ──────────────────────────────────────────────────────

/** Used in ProfilePage.tsx and others to fetch current user's events */
export const useGetMyEvents = (search = '', page = 1) => {
  return useQuery<UserEventsResponse>({
    queryKey: ['events', 'my', search, page],
    queryFn: async () => {
      const userId = localStorage.getItem('user_id');
      if (!userId) {
        console.warn('[API] useGetMyEvents: No user ID found');
        return { results: [], count: 0, next: null };
      }
      const params = new URLSearchParams({
        active_only: 'true',
        page: String(page),
        page_size: '20',
      });
      if (search.trim()) params.set('search', search.trim());
      console.log(`[API] Fetching my events via /profiles/events/${userId}/`);
      const { data } = await apiClient.get<UserEventsResponse>(
        `/profiles/events/${userId}/?${params.toString()}`,
      );
      console.log('[API] My events response:', data);
      return data;
    },
  });
};

/** Used in ServicesModal.tsx */
export const useGetMyServices = () => {
  return useQuery<UserServiceItem[]>({
    queryKey: ['services', 'my-selector'],
    queryFn: async () => {
      console.log('[API] Fetching my services for selector via /profiles/services/');
      const { data } = await apiClient.get('/profiles/services/');
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.results)) return data.results;
      if (Array.isArray(data?.data)) return data.data;
      return [];
    },
  });
};

/** Used in EventsModal.tsx */
export const useGetMyEventsInfinite = (search = '') => {
  return useInfiniteQuery<UserEventsResponse>({
    queryKey: ['events', 'my-infinite', search],
    queryFn: async ({ pageParam = 1 }) => {
      const userId = localStorage.getItem('user_id');
      if (!userId) return { results: [], count: 0, next: null };

      const params = new URLSearchParams({
        active_only: 'false',
        page: String(pageParam),
        page_size: '15',
      });
      if (search.trim()) params.set('search', search.trim());

      const { data } = await apiClient.get<UserEventsResponse>(
        `/profiles/events/${userId}/?${params.toString()}`,
      );
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;
      try {
        const url = new URL(lastPage.next);
        return parseInt(url.searchParams.get('page') || '1', 10);
      } catch {
        return undefined;
      }
    },
  });
};

/** Used in ProfilePage.tsx Gigs tab */
export const useGetUserEvents = (userId: string | number | undefined, page = 1) => {
  return useQuery<UserEventsResponse>({
    queryKey: ['events', 'user', userId, page],
    queryFn: async () => {
      if (!userId) return { results: [], count: 0, next: null };
      console.log(
        `[API] Fetching events for user ${userId} via /profiles/events/?user_id=${userId}`,
      );
      const { data } = await apiClient.get<UserEventsResponse>(
        `/profiles/events/?user_id=${userId}&page=${page}&page_size=20`,
      );
      console.log(`[API] User events response for ${userId}:`, data);
      return data;
    },
    enabled: !!userId,
  });
};

export interface CollaboratorFilters {
  search?: string;
  userType?: string;
  city?: string;
}

/** True when at least one filter is set — the collaborator list is never fetched unfiltered. */
export const hasCollaboratorFilters = ({ search, userType, city }: CollaboratorFilters) =>
  !!(search?.trim() || userType?.trim() || city?.trim());

/** Used in CollaboratorsSelector.tsx and ManageCollaboratorsModal.tsx */
export const useCollaboratorSearch = (filters: CollaboratorFilters) => {
  const search = filters.search?.trim() ?? '';
  const userType = filters.userType?.trim() ?? '';
  const city = filters.city?.trim() ?? '';
  return useInfiniteQuery({
    queryKey: ['users', 'collaborator-search', search, userType, city],
    enabled: hasCollaboratorFilters({ search, userType, city }),
    queryFn: async ({ pageParam = 1 }) => {
      const params = new URLSearchParams({ page: String(pageParam) });
      if (search) params.set('search', search);
      if (userType) params.set('user_type', userType);
      if (city) params.set('city', city);
      const { data } = await apiClient.get(`/custom_account/collaborator-user-filter/?${params}`);
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any) => {
      if (!lastPage?.next) return undefined;
      try {
        const url = new URL(lastPage.next);
        return parseInt(url.searchParams.get('page') || '1', 10);
      } catch {
        return undefined;
      }
    },
  });
};
